import { useEffect, useRef, useState } from 'react'

import AdomikLoader from 'src/components/ui/atoms/PlaceHolder/AdomikLoader'
import useQueryParams from 'src/hooks/useQueryParams'
import { parseURL } from 'src/utils/urls'
import AppRouter from './AppRouter'
import useAccount from './hooks/useAccount'
import useProductMatchParams from './hooks/useProductMatchParams'
import useSetDefaultCurrentOpenedProduct from './hooks/useSetDefaultCurrentOpenedProduct'
import useSettings from './hooks/useSettings'
import useMenusStore from './stores/useMenusStore'
import googleAnalytics from './utils/GoogleAnalytics'
import storageUtils from './utils/storageUtils'
import { isNeedToClearQueryFn } from './utils/urlUtils'

const App = () => {
  const {
    components,
    getComponents,
    getDimensionsColors,
    getDefaultDashboardToLoad,
    clearQueryParams,
  } = useSettings()
  const { queryParams, setQueryParams } = useQueryParams()
  const { informations: account, loginWithoutPrompt, isLogged } = useAccount()
  const [isDataInitialized, setIsDataInitialized] = useState(false)
  const [initialized, setInitialized] = useState(false)

  const initializedRef = useRef(false)

  const routeMatch = useProductMatchParams()

  const hasMenuLoaded = useMenusStore(state => Boolean(state.menus?.length))

  useSetDefaultCurrentOpenedProduct()

  const initialize = async () => {
    let userAccount = account

    try {
      // An expired token goes straight to the login page. Otherwise adomik_api
      // would still answer through the shared session cookie, the whole app
      // would render, and only analytics_api's 401 would send the user away.
      if (storageUtils.isAuthTokenExpired()) storageUtils.clearAuthToken()

      // Try to log the user
      if (storageUtils.getAuthToken()) {
        userAccount = await loginWithoutPrompt()

        localStorage.setItem('redirect', '')
      }

      // Init google analytics 4
      googleAnalytics.initialize({
        userEmail: userAccount?.email,
        sellerId: userAccount?.default_company?.name,
        userId: userAccount?.id,
      })
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error)
    } finally {
      setInitialized(true)
    }
  }

  useEffect(() => {
    setTimeout(async () => await initialize(), 0)
  }, [])

  // Load data after login
  useEffect(() => {
    const handleInitializeData = async () => {
      const promises: Promise<any>[] = [getComponents(), getDimensionsColors()]

      const pathname = window.location.pathname

      const { page } = parseURL(pathname)
      const from = queryParams?.from
      const downloadPdf = queryParams.download_pdf

      // If we come from the "launch_in (Ecosystems) or shared_link", no need to clear the queryParams, we need It !
      isNeedToClearQueryFn(from) && clearQueryParams()
      const programId = routeMatch?.params?.program
      // If we come from the "download PDF" mode, there is no need to make a new requests.
      const [menus] = await Promise.all(promises)

      if (from !== 'download_pdf' && !downloadPdf) {
        await getDefaultDashboardToLoad({
          subProduct: page,
          enableLoading: false,
          resetSettings: true,
          programId,
          menus,
        })
      }

      // Reset certains queries params
      isNeedToClearQueryFn(from) && setQueryParams({ from: '' })
      downloadPdf && setQueryParams({ download_pdf: false })
    }

    if (initialized && !isDataInitialized && !initializedRef.current) {
      if (account) {
        initializedRef.current = true
        handleInitializeData().finally(() => setIsDataInitialized(true))
        return
      }
      initializedRef.current = true
      setIsDataInitialized(true)
    }
  }, [initialized, account, queryParams?.from, queryParams.download_pdf, isDataInitialized])

  return !isDataInitialized || (isLogged && (!components || !hasMenuLoaded || !account)) ? (
    <AdomikLoader />
  ) : (
    <AppRouter />
  )
}

export default App
